import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Modal,
  Backdrop,
  Typography,
  useTheme,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  useMediaQuery,
} from "@mui/material";
import SearchBar from "../common-components/Search-bar"; 
import { useSearchParams } from "react-router-dom";
import {
  Add,
  CalendarToday,
  Flag,
  InfoOutlined,
  AccessTime,
  PlayCircleOutline,
  CheckCircleOutline,
  WarningAmber as WarningIcon
} from "@mui/icons-material";
import TaskCreationForm from "./TaskCreationForm"; 
import ApiClient from "../../apis/Api-client"; 
import { END_POINT } from "../../utils/End-points"; 
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { successToster, errorToster } from "../../shared/toster/Toster"; 
import EmployeeTaskDownload from "./EmployeeTaskDownload"; 
import { useTranslation } from "react-i18next";
import { Helper } from "../../utils/Helpers"; 

import { 
  TASK_LOAD_LIMIT, 
  TASK_VIEW_CONFIG 
} from "../../utils/Constants";


const TASK_CARD_STYLES = {
  todo: {
    color: "#F7A86E",
    border: "#FFE29E",
    bg: "#FFFBF1",
    countBg: "#FFFBF1",
  },
  inProgress: {
    color: "#6372FF",
    border: "#BFC5FF",
    bg: "#F3F8FF",
    countBg: "#F3F8FF",
  },
  done: {
    color: "#62BA60",
    border: "#A8F3A6",
    bg: "#F1FFF1",
    countBg: "#F1FFF1",
  },
};

const TASK_MODAL_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "85%", md: "600px" }, 
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  maxHeight: "90vh",
  overflowY: "auto",
  p: { xs: 2, sm: 4 }
};

const EmployeeCreateTask = () => {
  const { api } = ApiClient();
  const theme = useTheme();
  const isMob = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const helper = new Helper();

  const [open, setOpen] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  
  const showDownload = searchParams.get(TASK_VIEW_CONFIG.PARAM) === TASK_VIEW_CONFIG.DOWNLOAD_MODE;

  const [columns, setColumns] = useState({
    todo: { id: "todo", title: "To do", items: [], page: 1, hasMore: true, loading: false, totalCount: 0 },
    inProgress: { id: "inProgress", title: "In Progress", items: [], page: 1, hasMore: true, loading: false, totalCount: 0 },
    done: { id: "done", title: "Completed", items: [], page: 1, hasMore: true, loading: false, totalCount: 0 },
  });

  const isFetchingRef = useRef({ todo: false, inProgress: false, done: false });

  const checkIsOverdue = (task) => {
    if (!task.due_at || task.status === "Done") return false;
    return new Date(task.due_at) < new Date(); 
  };

  const sortTasks = (items, columnId) => {
    if (columnId === "done") return items;

    return [...items].sort((a, b) => {
      const aOverdue = checkIsOverdue(a);
      const bOverdue = checkIsOverdue(b);
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;
      return 0;
    });
  };

  useEffect(() => {
    if (!showDownload) {
      const delay = searchKey ? 1000 : 0;
      const timer = setTimeout(() => {
        fetchColumnData("todo", 1, true);
        fetchColumnData("inProgress", 1, true);
        fetchColumnData("done", 1, true);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [searchKey, showDownload]);

  const fetchColumnData = async (statusId, pageNum, isReset = false) => {
    if (isFetchingRef.current[statusId] && !isReset) return; 
    
    isFetchingRef.current[statusId] = true;
    setColumns(prev => ({
        ...prev,
        [statusId]: { ...prev[statusId], loading: true }
    }));

    try {
      const apiStatus = statusId === "todo" ? "Todo" : statusId === "inProgress" ? "InProgress" : "Done";
      
      const response = await api.get(
        `${END_POINT.EMPLOYEE.GET_ALL_TASKS}?page=${pageNum}&limit=${TASK_LOAD_LIMIT}&search=${searchKey}&status=${apiStatus}`
      );

      if (response.data?.status || response.status === 200) {
        const newTasksFromApi = response.data.data.data || [];
        const total = response.data.data.total || 0;
        
        setColumns(prev => {
            const currentItems = isReset ? [] : prev[statusId].items;
            const existingIds = new Set(currentItems.map(i => i.task_id));
            const filteredNew = newTasksFromApi.filter(i => !existingIds.has(i.task_id));

            const combinedItems = [...currentItems, ...filteredNew];
            const sortedItems = sortTasks(combinedItems, statusId);

            return {
                ...prev,
                [statusId]: {
                    ...prev[statusId],
                    items: sortedItems,
                    page: pageNum,
                    hasMore: sortedItems.length < total,
                    loading: false,
                    totalCount: total 
                }
            };
        });
      }
    } catch (error) {
      console.error(`Error fetching ${statusId}:`, error);
      setColumns(prev => ({
        ...prev,
        [statusId]: { ...prev[statusId], loading: false }
      }));
    } finally {
      isFetchingRef.current[statusId] = false;
    }
  };

  const handleColumnScroll = (e, statusId) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      const col = columns[statusId];
      if (col.hasMore && !col.loading) {
        fetchColumnData(statusId, col.page + 1, false);
      }
    }
  };

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
        return; 
    }

    const sourceCol = columns[source.droppableId];
    const destCol = columns[destination.droppableId];
    
    const sourceItems = [...sourceCol.items];
    const [removed] = sourceItems.splice(source.index, 1);
    
    const newStatus = destination.droppableId === "todo" ? "Todo" : destination.droppableId === "inProgress" ? "InProgress" : "Done";
    const nowISO = new Date().toISOString();
    
    const updatedTask = { ...removed, status: newStatus, moved_now: Date.now() };

    if (newStatus === "InProgress") {
        if (!updatedTask.started_at) updatedTask.started_at = nowISO;
        if (source.droppableId === "done") updatedTask.completed_at = null;
    } else if (newStatus === "Done") {
        if (!updatedTask.completed_at) updatedTask.completed_at = nowISO;
    } else if (newStatus === "Todo") {
        updatedTask.started_at = null;
        updatedTask.completed_at = null;
    }

    let finalDestItems = [];
    const destItems = [...destCol.items]; 

    if (destination.droppableId === "done") {
        finalDestItems = [updatedTask, ...destItems];
    } else {
        const existingOverdue = destItems.filter(t => checkIsOverdue(t));
        const existingNormal = destItems.filter(t => !checkIsOverdue(t));

        if (checkIsOverdue(updatedTask)) {
            finalDestItems = [updatedTask, ...existingOverdue, ...existingNormal];
        } else {
            finalDestItems = [...existingOverdue, updatedTask, ...existingNormal];
        }
    }

    setColumns({
        ...columns,
        [source.droppableId]: {
            ...sourceCol,
            items: sourceItems,
            totalCount: Math.max(0, sourceCol.totalCount - 1)
        },
        [destination.droppableId]: {
            ...destCol,
            items: finalDestItems, 
            totalCount: destCol.totalCount + 1
        }
    });

    try {
        let payload = { 
            task_id: draggableId, 
            status: newStatus,
            started_at: updatedTask.started_at,
            completed_at: updatedTask.completed_at
        };
        await api.put(END_POINT.EMPLOYEE.UPDATE_TASK_STATUS, payload);
        successToster(t("pages.task_management.status_updated"));
    } catch (error) {
        console.error("Update failed", error);
        errorToster(t("pages.task_management.update_failed"));
        fetchColumnData(source.droppableId, 1, true);
        fetchColumnData(destination.droppableId, 1, true);
    }
  };

  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: "none",
    marginBottom: "16px",
    transform: isDragging ? "scale(1.02)" : "scale(1)",
    boxShadow: isDragging ? "0px 10px 20px rgba(0,0,0,0.15)" : "none",
    transition: "transform 0.2s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.2s",
    ...draggableStyle,
  });

  const handleOpenTable = () => setSearchParams({ [TASK_VIEW_CONFIG.PARAM]: TASK_VIEW_CONFIG.DOWNLOAD_MODE });
  
  const handleCloseTable = () => {
    setSearchParams({});
    fetchColumnData("todo", 1, true);
    fetchColumnData("inProgress", 1, true);
    fetchColumnData("done", 1, true);
  };

  const handleCreateOpen = async () => {
    setOpen(true);
    try {
      const response = await api.get(END_POINT.EMPLOYEE.GET_ALL_EMPLOYEES);
      if (response.data?.data) setEmployeeList(response.data.data);
    } catch (error) { console.error(error); }
  };

  const handleCreateClose = () => {
      setOpen(false);
      fetchColumnData("todo", 1, true); 
  };

  const renderTimestamps = (task) => {
     const timestamps = [];
    if (task.assigned_at) {
      timestamps.push({
        label: t("pages.task_management.assigned"),
        time: helper.formatTimestampForCard(task.assigned_at),
        icon: <AccessTime sx={{ fontSize: { xs: "0.85rem", sm: "0.85rem", md: "0.85rem" }, color: "#F7A86E" }} />,
      });
    }
    if ((task.status === "InProgress" || task.status === "Done") && task.started_at) {
      timestamps.push({
        label: t("pages.task_management.started"),
        time: helper.formatTimestampForCard(task.started_at),
        icon: <PlayCircleOutline sx={{ fontSize: { xs: "0.85rem", sm: "0.85rem", md: "0.85rem" }, color: "#6372FF" }} />,
      });
    }
    if (task.status === "Done" && task.completed_at) {
      timestamps.push({
        label: t("pages.task_management.finished"),
        time: helper.formatTimestampForCard(task.completed_at),
        icon: <CheckCircleOutline sx={{ fontSize: { xs: "0.85rem", sm: "0.85rem", md: "0.85rem" }, color: "#62BA60" }} />,
      });
    }
    return timestamps.map((ts, i) => (
      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, minWidth: 0, overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{ts.icon}</Box>
        <Typography variant="caption" sx={{ fontSize: { xs: "0.7rem", sm: "0.7rem", md: "0.72rem" }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
          <Box component="span" sx={{ fontWeight: 600, color: "#777" }}>{ts.label}</Box>{" "}
          <Box component="span" sx={{ fontWeight: 500 }}>{ts.time}</Box>
        </Typography>
      </Box>
    ));
  };

  if (showDownload) return <EmployeeTaskDownload handleBack={handleCloseTable} />;

  return (
    <Box sx={{
        border: "1px solid #FFE097", borderRadius: "8px", width: { xs: "95%", sm: "97%" },
        margin: "1% auto", minHeight: { xs: "88vh", sm: "89vh", md: "86.23vh" }, bgcolor: "#FFF",
        height: { xs: "88vh", sm: "89vh", md: "82vh" }, overflowY: "hidden",
        display: "flex", flexDirection: "column"
      }}>
      
      {/* --- HEADER SECTION --- */}
      <Grid container sx={{ backgroundColor: "#FFF6E8", padding: { xs: "12px", sm: "16px 12px" }, borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }} alignItems={"center"} justifyContent={"space-between"}>
        <Grid item xs={12} sm={5} md={4} sx={{ mb: { xs: 1.5, sm: 0 }, width: "100%" }}>
          <SearchBar startAdornment={true} placeholder={t("pages.task_management.search_placeholder")} height={{ xs: "40px", sm: "45px", md: "40px" }} setSearchKey={setSearchKey} searchKey={searchKey} handleSearch={() => {}} />
        </Grid>
        <Grid item xs={12} sm={6.5} md={7} sx={{ display: "flex", justifyContent: { xs: "space-between", sm: "flex-end" }, gap: "8px", width: "100%" }}>
          <Button 
            variant="contained" 
            size="small" 
            onClick={handleOpenTable} 
            sx={{ 
              textTransform: "none",
              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" }, 
              flex: { xs: 1, sm: "initial" }, 
              py: { xs: 0.8, sm: 1 },
              px: { xs: 1, sm: 2 },
              minWidth: "fit-content",
              whiteSpace: "nowrap"
            }}
          >
            {t("pages.task_management.print_details")}
          </Button>
          <Button 
            variant="contained" 
            size="small" 
            onClick={handleCreateOpen} 
            startIcon={<Add sx={{ fontSize: { xs: "1.2rem", sm: "1.3rem", md: "1.4rem" } }} />} 
            sx={{ 
              textTransform: "none",
              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" }, 
              flex: { xs: 1, sm: "initial" }, 
              py: { xs: 0.8, sm: 1 },
              px: { xs: 1, sm: 2 },
              minWidth: "fit-content",
              whiteSpace: "nowrap"
            }}
          >
            {t("pages.task_management.create_task")}
          </Button>
        </Grid>
      </Grid>

      {/* --- KANBAN BOARD SECTION --- */}
      <Box sx={{ width: "100%", overflowX: { xs: "auto", sm: "hidden" }, height: "100%", flexGrow: 1 }}>
        <Grid container sx={{ backgroundColor: "#FFFFFF", py: "2%", flexWrap: { xs: "nowrap", sm: "nowrap" }, minWidth: "100%", justifyContent: { xs: "flex-start", sm: "center" }, gap: { xs: 2, sm: 0 } }}>
          <DragDropContext onDragEnd={onDragEnd}>
            {Object.values(columns).map((column, colIndex) => (
              <Grid item sx={{
                  minWidth: { xs: "88vw", sm: 0 },
                  width: { sm: "32%" },
                  flex: { xs: "0 0 auto", sm: "1 1 0" },
                  margin: { sm: "0 0.5%" },
                  borderRight: { sm: colIndex !== 2 ? "2px solid #FFE29E" : "none" },
                  paddingRight: { sm: colIndex !== 2 ? "2%" : 0 },
                  paddingLeft: { xs: "2%", sm: 0 }
                }} key={column.id}>
                
                <Typography sx={{ 
                  textAlign: "center", 
                  fontWeight: "bold", 
                  mb: 2, 
                  position: "sticky", 
                  top: 0, 
                  bgcolor: "white", 
                  zIndex: 1, 
                  py: 1, 
                  fontSize: { xs: "1.1rem", sm: "1.05rem", md: "1.3rem" } 
                }}>
                  {column.title} ({column.totalCount || 0})
                </Typography>

                <Droppable droppableId={column.id}>
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      onScroll={(e) => handleColumnScroll(e, column.id)}
                      sx={{
                        height: { xs: "74vh", sm: "75vh", md: "70vh" },
                        overflowY: { xs: "auto", md: "auto" },
                        overflowX: "hidden",
                        bgcolor: isMob ? "#fafafa" : "transparent",
                        borderRadius: 2,
                        p: isMob ? "3%" : 0,
                        "&::-webkit-scrollbar": { width: "6px" },
                        "&::-webkit-scrollbar-thumb": { backgroundColor: "#ccc", borderRadius: "4px" }
                      }}
                    >
                      {column.items.map((task, idx) => {
                        const isOverdue = checkIsOverdue(task);
                        const isDueToday = helper.isTaskDueToday(task);
                        
                        return (
                          <Draggable key={String(task.task_id)} draggableId={String(task.task_id)} index={idx}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                              >
                                <Card sx={{
                                    bgcolor: isOverdue ? "#FFF0F0" : TASK_CARD_STYLES[column.id].bg,
                                    border: isOverdue ? "1px solid #FFCDD2" : `1px solid ${TASK_CARD_STYLES[column.id].border}`,
                                    borderRadius: "14px",
                                    boxShadow: snapshot.isDragging ? 4 : 1,
                                    mb: 2
                                  }}>
                                  <CardContent sx={{ p: "16px !important" }}>
                                    
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                                        <Avatar style={{ backgroundColor: "#F7A86E" }} sx={{ width: { xs: "32px", sm: "30px", md: "30px" }, height: { xs: "32px", sm: "30px", md: "30px" }, fontSize: { xs: "0.85rem", sm: "0.85rem", md: "0.85rem" }, color: "#fff" }}>
                                          {Array.isArray(task.assigned_to_name) ? (task.assigned_to_name[0] ? task.assigned_to_name[0][0] : "U") : "U"}
                                        </Avatar>
                                        
                                        <Typography variant="body1" fontWeight={700} noWrap sx={{ flex: 1, fontSize: { xs: "0.85rem", sm: "0.85rem", md: "0.85rem" } }}>
                                          {Array.isArray(task.assigned_to_name) ? task.assigned_to_name.join(", ") : (task.assigned_to_name || "Unknown")}
                                        </Typography>
                                      </Box>

                                      <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                                        {/* Reduced tablet (sm) font size here */}
                                        {isOverdue && <Chip icon={<WarningIcon style={{ fontSize: "inherit", color: "white" }} />} label="Overdue" size="small" sx={{ bgcolor: "#D32F2F", color: "white", fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" }, height: "auto", borderRadius: "6px", fontWeight: "bold", py: "2px", px: "4px", "& .MuiChip-icon": { fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" } } }} />}
                                        {isDueToday && task.status !== "Done" && <Chip label={t("pages.task_management.due_soon")} size="small" sx={{ bgcolor: "#FF9800", color: "white", fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" }, height: "auto", borderRadius: "6px", py: "2px", px: "4px" }} />}
                                      </Box>
                                    </Box>

                                    <Box sx={{ mt: 0.5 }}>{renderTimestamps(task)}</Box>
                                    
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8, color: isOverdue ? "#D32F2F" : "#555", mt: 1, minWidth: 0, overflow: "hidden" }}>
                                      <CalendarToday sx={{ fontSize: { xs: "1.1rem", sm: "1rem", md: "1rem" }, flexShrink: 0 }} />
                                      <Typography variant="caption" fontWeight={500} sx={{ fontSize: { xs: "0.75rem", sm: "0.75rem", md: "0.75rem" }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                                        <span style={{ marginLeft: "4px", fontWeight: "bold", color: isOverdue ? "#D32F2F" : "#333" }}>{t("pages.task_management.due")} {helper.formatTimestampForCard(task.due_at)}</span>
                                      </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                                      <Flag sx={{ fontSize: { xs: "1.1rem", sm: "1rem", md: "1rem" }, color: helper.getPriorityColor(task.priority), flexShrink: 0 }} />
                                      <Typography variant="caption" sx={{ color: helper.getPriorityColor(task.priority), fontWeight: 700, fontSize: { xs: "0.75rem", sm: "0.75rem", md: "0.75rem" } }}>{task.priority?.toUpperCase()}</Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: "flex", alignItems: "start", gap: 1, color: "#666", mt: 0.8 }}>
                                      <InfoOutlined sx={{ fontSize: { xs: "1.1rem", sm: "1rem", md: "1rem" }, mt: "2px", flexShrink: 0 }} />
                                      <Typography variant="caption" sx={{ lineHeight: 1.4, fontSize: { xs: "0.75rem", sm: "0.75rem", md: "0.75rem" }, wordBreak: "break-word" }}>
                                        {task.task_description?.length > 50 ? task.task_description.substring(0, 50) + "..." : task.task_description}
                                      </Typography>
                                    </Box>
                                  </CardContent>
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                      {column.loading && <Typography align="center" variant="caption" display="block" sx={{ py: 2, fontSize: "0.85rem" }}>Loading...</Typography>}
                    </Box>
                  )}
                </Droppable>
              </Grid>
            ))}
          </DragDropContext>
        </Grid>
      </Box>

      <Modal open={open} onClose={handleCreateClose} slots={{ backdrop: Backdrop }}>
        <Box sx={TASK_MODAL_STYLE}> 
          <TaskCreationForm handleClose={handleCreateClose} employeeList={employeeList} />
        </Box>
      </Modal>
    </Box>
  );
};

export default EmployeeCreateTask;